import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// Global event for app becoming active - components can subscribe to this
const appActiveListeners = new Set();
export const onAppActive = (callback) => {
  appActiveListeners.add(callback);
  return () => appActiveListeners.delete(callback);
};
// Emit to all subscribers when tab becomes visible after being away
const emitAppActive = () => {
  appActiveListeners.forEach(cb => {
    try { cb(); } catch (e) { console.error('App active listener error:', e); }
  });
};

// Tier limits configuration (funnels mirrors TIER_LIMITS in api/funnels.js)
const TIER_LIMITS = {
  trial:      { landing_pages: -1, custom_domains: 5,  active_tests: -1, funnels: -1, data_days: 90, full_ab_testing: true  },
  starter:    { landing_pages: 3,  custom_domains: 1,  active_tests: 2,  funnels: 1,  data_days: 7,  full_ab_testing: false },
  pro:        { landing_pages: -1, custom_domains: 5,  active_tests: -1, funnels: -1, data_days: 90, full_ab_testing: true  },
  admin:      { landing_pages: -1, custom_domains: -1, active_tests: -1, funnels: -1, data_days: -1, full_ab_testing: true  },
  superadmin: { landing_pages: -1, custom_domains: -1, active_tests: -1, funnels: -1, data_days: -1, full_ab_testing: true  },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAppActive, setIsAppActive] = useState(true);

  const initialized           = useRef(false);
  // FIX 3: use useRef so lastHiddenTime persists across renders
  // (plain `let` inside useEffect resets whenever the effect re-runs due to [user] change)
  const lastHiddenTime        = useRef(Date.now());
  const lastVisibilityRefresh = useRef(0);
  const isRefreshing          = useRef(false);

  // Derived state
  const isAdmin = profile?.role === 'admin' || profile?.role === 'superadmin';
  const tier    = profile?.tier || 'trial';
  const isDemo  = !!profile?.is_demo;
  const limits  = TIER_LIMITS[tier] || TIER_LIMITS.trial;

  const trialEndsAt    = profile?.trial_ends_at ? new Date(profile.trial_ends_at) : null;
  const now            = new Date();
  const daysRemaining  = trialEndsAt ? Math.max(0, Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24))) : 0;
  const isTrialActive  = !isAdmin && tier === 'trial' && daysRemaining > 0;
  const isTrialExpired = !isAdmin && tier === 'trial' && daysRemaining <= 0;
  const isPro          = tier === 'pro' || isAdmin;
  const isStarter      = tier === 'starter';
  const hasAccess      = isAdmin || isDemo || isPro || isStarter || isTrialActive;

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return null;
    try {
      const { data, error } = await supabase
        .from('profiles').select('*').eq('id', userId).single();
      if (error) {
        if (error.code !== 'PGRST116' && !error.message?.includes('AbortError'))
          console.error('Profile fetch error:', error);
        return null;
      }
      return data;
    } catch (e) {
      if (e.message?.includes('AbortError')) return null;
      console.error('Profile fetch exception:', e);
      return null;
    }
  }, []);

  const createProfileIfNeeded = useCallback(async (authUser) => {
    if (!authUser) return null;
    let profileData = await fetchProfile(authUser.id);
    if (!profileData) {
      const trialEnd = new Date();
      trialEnd.setDate(trialEnd.getDate() + 30);
      const { error: createError } = await supabase.from('profiles').upsert({
        id: authUser.id,
        email: authUser.email,
        username: authUser.email?.split('@')[0] || 'user',
        tier: 'trial',
        trial_ends_at: trialEnd.toISOString(),
      }, { onConflict: 'id' });
      if (createError && !createError.message?.includes('AbortError'))
        console.error('Profile creation error:', createError);
      else
        profileData = await fetchProfile(authUser.id);
    }
    return profileData;
  }, [fetchProfile]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      const data = await fetchProfile(user.id);
      if (data) setProfile(data);
      return data;
    }
    return null;
  }, [user, fetchProfile]);

  // ── Visibility-based session refresh ──────────────────────────────────────
  // FIX 1: use refreshSession() not getSession()
  //   getSession() reads from localStorage — returns the EXPIRED token from cache
  //   refreshSession() contacts the Supabase auth server and gets a new JWT
  //
  // FIX 2: threshold aligned to 10s (same as useVisibilityRefresh hook)
  //   Old value was 60s — data re-fetches fired at 10s but JWT refresh at 60s,
  //   so the first 50 seconds of fetches ran with an expired token
  //
  // FIX 3: lastHiddenTime is now a useRef (see declaration above)
  //   Old plain `let` was trapped in the effect closure and reset whenever
  //   the effect re-ran due to [user] dependency change
  //
  // FIX 4: TOKEN_REFRESHED sets user only if id changed (avoids new object ref)
  //   See onAuthStateChange handler below
  //
  // FIX 5: emitAppActive() is now called — wires up useAppActive subscribers
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenTime.current = Date.now();
        setIsAppActive(false);
        return;
      }

      // Tab is now visible
      setIsAppActive(true);
      const hiddenMs = Date.now() - lastHiddenTime.current;

      // Only act if tab was hidden for more than 10 seconds
      if (hiddenMs < 10000) return;

      // Debounce: don't overlap or repeat within 10s
      const nowTs = Date.now();
      if (nowTs - lastVisibilityRefresh.current < 10000) return;
      if (isRefreshing.current) return;

      lastVisibilityRefresh.current = nowTs;
      isRefreshing.current = true;

      try {
        // FIX 1: refreshSession() — actually contacts server, gets a new JWT
        const { data, error } = await supabase.auth.refreshSession();

        if (error) {
          console.debug('[Auth] Session refresh failed:', error.message);
          // If the refresh token itself expired, sign out gracefully
          if (error.message?.includes('refresh_token') ||
              error.message?.includes('Invalid Refresh Token') ||
              error.status === 400) {
            console.warn('[Auth] Refresh token expired — signing out');
            await supabase.auth.signOut();
          }
        } else if (data?.session) {
          // FIX 4: only call setUser if it's actually a different session
          // Avoids creating a new user object reference that would cause
          // useCallback([user]) deps in page components to re-fire loadData
          if (!user || data.session.user.id !== user.id) {
            setUser(data.session.user);
          }
          // Session refreshed — emit to all useAppActive subscribers
          // FIX 5: emitAppActive was defined but never called
          emitAppActive();
        }
      } catch (e) {
        console.debug('[Auth] Visibility refresh error:', e.message);
      } finally {
        isRefreshing.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]); // user needed for the id comparison in FIX 4

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    let subscription = null;

    const initialize = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) { console.error('Session error:', error); setLoading(false); return; }

        if (session?.user) {
          setUser(session.user);
          const profileData = await createProfileIfNeeded(session.user);
          setProfile(profileData);
        }
        setLoading(false);

        const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (event === 'INITIAL_SESSION') return;

          if (event === 'SIGNED_IN' && session?.user) {
            // CRITICAL: SIGNED_IN fires on every tab return (Supabase realtime reconnect).
            // If the same user is already loaded, do absolutely nothing — any state
            // change here creates new object references that cascade through every
            // useCallback/useEffect with [user] deps across the whole app, causing
            // components to re-fetch and show spinners on every tab switch.
            setUser(prev => {
              if (prev?.id === session.user.id) return prev; // same user — no change, no cascade
              // Genuinely new user — fetch their profile too
              createProfileIfNeeded(session.user).then(data => {
                if (data) setProfile(data);
              });
              return session.user;
            });
          } else if (event === 'SIGNED_OUT') {
            setUser(null);
            setProfile(null);
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            // FIX 4: don't create a new user object if it's the same user
            // This prevents useCallback([user]) deps from firing unnecessarily
            setUser(prev => {
              if (prev?.id === session.user.id) return prev; // same user — keep same reference
              return session.user; // different user (unlikely but safe)
            });
          } else if (event === 'USER_UPDATED' && session?.user) {
            setUser(session.user);
            const profileData = await fetchProfile(session.user.id);
            setProfile(profileData);
          }
        });

        subscription = data.subscription;
      } catch (e) {
        console.error('Auth initialization error:', e);
        setLoading(false);
      }
    };

    initialize();
    return () => { if (subscription) subscription.unsubscribe(); };
  }, [fetchProfile, createProfileIfNeeded]);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
    } catch (e) {
      console.error('Sign out error:', e);
    }
  };

  const canCreate = (resourceType) => {
    if (isAdmin) return true;
    if (!hasAccess) return false;
    const limit = limits[resourceType];
    if (limit === -1) return true;
    return true;
  };

  const canUse = (feature) => {
    if (isAdmin) return true;
    if (!hasAccess) return false;
    switch (feature) {
      case 'full_ab_testing':    return limits.full_ab_testing;
      case 'custom_domains':     return limits.custom_domains > 0 || limits.custom_domains === -1;
      case 'roi_alerts':         return isPro;
      case 'profitability_scoring': return isPro;
      default:                   return true;
    }
  };

  const value = {
    user, profile, loading, isAdmin, isPro, isStarter, tier, limits,
    trialEndsAt, daysRemaining, isTrialActive, isTrialExpired,
    hasAccess, isDemo, isAppActive,
    signOut, refreshProfile, canCreate, canUse,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to subscribe to app becoming active (for data refresh)
// Now correctly wired — emitAppActive() is called in the visibilitychange handler
export const useAppActive = (callback) => {
  useEffect(() => {
    if (!callback) return;
    return onAppActive(callback);
  }, [callback]);
};

export default AuthContext;
